import {
    ActionDescription,
    ActionKind,
    InputOutputTables,
    PropertyDescription,
    TableColumn,
    MutatingScreenKind,
} from "../description";
import { ActionDescriptor } from "./action-descriptor";
import { SerializedComponent } from "../../components/SerializedApp";
import { panic } from "../../Support";
import {
    ComponentBuilder,
    ComponentConfiguratorContext,
    PropertyDescriptor,
    WireActionHydrator,
    WireInflationBackend,
} from "../components";
import { makeColumnProperty } from "../components/property-utils";
import { BaseActionHandler, tokenForProperty } from "./base";
import { DescriptionToken } from "./action-handler";
import { makeTextPropertyDescriptor } from "../components/descriptor-utils";
import { makeText } from "../builder-utils";
import { makeTelephoneURL } from "../../urls";
import { asString } from "../../computation-model/data";

interface PhoneCallActionDescription extends ActionDescription {
    readonly kind: ActionKind.PhoneCallWithArgs;
    readonly number?: PropertyDescription;
}

export function makePhoneNumberPropertyDescriptor(
    mutatingScreenKind: MutatingScreenKind | undefined
): PropertyDescriptor {
    return makeTextPropertyDescriptor("number", "Phone number", "Enter phone number", true, mutatingScreenKind, {
        preferredNames: ["phone", "mobile", "number"],
        preferredType: "phone-number",
        searchable: false,
        columnFirst: true,
        applyFormat: false,
    });
}

export class PhoneCallWithArgsHandler extends BaseActionHandler<PhoneCallActionDescription> {
    public readonly kind = ActionKind.PhoneCallWithArgs;

    public readonly iconName = "phone";

    public getDescriptor(
        _desc: PhoneCallActionDescription | undefined,
        _tables: InputOutputTables | undefined,
        _ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ActionDescriptor {
        return {
            name: "Dial Phone Number",
            needsScreenContext: true,
            properties: [makePhoneNumberPropertyDescriptor(mutatingScreenKind)],
        };
    }

    public newDefaultActionDescription(c: TableColumn): PhoneCallActionDescription | undefined {
        if (c.type.kind !== "phone-number") return undefined;
        return { kind: ActionKind.PhoneCallWithArgs, number: makeColumnProperty(c.name) };
    }

    public makeAction(
        builder: ComponentBuilder,
        desc: PhoneCallActionDescription,
        tables: InputOutputTables,
        screenContext: SerializedComponent | undefined
    ): SerializedComponent | undefined {
        const { b } = builder;
        if (screenContext === undefined) {
            return panic("Must have screen context for Dial Phone Number action");
        }

        const number = makeText(builder, desc.number, tables.input, true, false, false, false, screenContext);
        if (number === undefined) return undefined;

        return b.actionIfContextDefined(b.phoneCall(number));
    }

    public getTokenizedDescription(
        desc: PhoneCallActionDescription,
        tables: InputOutputTables,
        ccc: ComponentConfiguratorContext
    ): readonly DescriptionToken[] | undefined {
        const columnToken = tokenForProperty(desc.number, tables, ccc);
        if (columnToken === undefined) return undefined;

        return [columnToken];
    }

    public inflate(ib: WireInflationBackend, desc: PhoneCallActionDescription): WireActionHydrator | undefined {
        const [numberGetter] = ib.getValueGetterForProperty(desc.number, false);

        return vp => {
            const number = asString(numberGetter(vp));

            const url = makeTelephoneURL(number);
            if (url === undefined) return undefined;

            return async ab => {
                ab.actionCallbacks.openLink(url);
                return true;
            };
        };
    }
}
